import { useEffect, useState } from 'react';
import { describePushResult } from '../components/ChannelToggles';
import { useNavigate } from 'react-router-dom';
import { api } from '../utils/api';
import {
  BookOpen, MessageCircle, DollarSign, Heart, Lock, FileText, Mail,
  Users, Video, Quote, HelpCircle, Lightbulb, Bell, Sparkles,
  ChevronDown, LayoutGrid, PenSquare, Waves, Mountain, Menu, X,
  BarChart3, Wallet, CalendarClock, Package, User, ShieldCheck, MessageSquareText, Tag, LifeBuoy,
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { toast } from 'sonner';

import {
  OverviewTab, ArticlesTab, CategoriesTab, FeelingsTab, ConsultationsTab,
  PrayerDigestTab, VideoTab, ScriptureLibraryTab, ContactMessagesTab,
  FAQsTab, TopicsTab, EngagementTab, StoriesTab, VerseOfDayTab, MembersTab,
} from './admin/AdminTabs';
import AnalyticsTab from './admin/AnalyticsTab';
import EarningsTab from './admin/EarningsTab';
import ContentCalendarTab from './admin/ContentCalendarTab';
import AlertsPanel from './admin/AlertsPanel';

import {
  ArticleFormModal, FeelingFormModal, CategoryFormModal,
  ScriptureFormModal, NotifyModal, FAQFormModal,
} from './admin/AdminModals';

// Nature-tinted color palette per section group
const NAV_GROUPS = [
  {
    key: 'overview',
    label: 'Overview',
    Icon: LayoutGrid,
    tint: '#5B7C99',
    accent: 'ocean',
    tabs: [
      { key: 'overview', label: 'Dashboard', Icon: LayoutGrid },
      { key: 'calendar', label: 'Content Calendar', Icon: CalendarClock },
      { key: 'analytics', label: 'Reading Trends', Icon: BarChart3 },
    ],
  },
  {
    key: 'content',
    label: 'Content Studio',
    Icon: PenSquare,
    tint: '#78A18E',
    accent: 'forest',
    tabs: [
      { key: 'articles', label: 'Articles', Icon: BookOpen },
      { key: 'verse', label: 'Verse of Week', Icon: Sparkles },
      { key: 'categories', label: 'Categories', Icon: Heart },
      { key: 'feelings', label: 'Feelings Guidance', Icon: Heart },
      { key: 'library', label: 'Scripture Library', Icon: BookOpen },
      { key: 'topics', label: 'Topic Ideas', Icon: Lightbulb },
      { key: 'faqs', label: 'FAQ', Icon: HelpCircle },
    ],
  },
  {
    key: 'community',
    label: 'Community',
    Icon: Users,
    tint: '#E57A44',
    accent: 'sunrise',
    tabs: [
      { key: 'consultations', label: 'Consultations', Icon: DollarSign },
      { key: 'earnings', label: 'Earnings Wallet', Icon: Wallet },
      { key: 'digest', label: 'Prayer Digest', Icon: Mail },
      { key: 'prayer-emails', label: 'Prayer Emails', Icon: Sparkles, href: '/admin/prayer-emails' },
      { key: 'subscribers', label: 'Subscribers', Icon: Mail, href: '/admin/subscribers' },
      { key: 'engagement', label: 'Engagement Digest', Icon: Bell },
      { key: 'stories', label: 'Testimonials', Icon: Quote },
      { key: 'members', label: 'Members', Icon: Users },
    ],
  },
  {
    key: 'site',
    label: 'Site & Inbox',
    Icon: Mountain,
    tint: '#B77BB0',
    accent: 'twilight',
    tabs: [
      { key: 'video', label: 'Intro Video', Icon: Video },
      { key: 'messages', label: 'Contact Messages', Icon: MessageCircle },
      { key: 'resources', label: 'Resources', Icon: LifeBuoy, href: '/admin/resources' },
    ],
  },
];

const TAB_TO_GROUP = NAV_GROUPS.reduce((acc, g) => {
  g.tabs.forEach(t => { acc[t.key] = g; });
  return acc;
}, {});

export default function Admin() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [expandedGroups, setExpandedGroups] = useState({ overview: true, content: true, community: true, site: true });
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // Core data
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [consultations, setConsultations] = useState([]);
  const [feelings, setFeelings] = useState([]);
  const [stats, setStats] = useState({ articles: 0, consultations: 0, revenue: 0 });

  // Form / modal state
  const [editingArticle, setEditingArticle] = useState(null);
  const [editingFeeling, setEditingFeeling] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  const [showArticleForm, setShowArticleForm] = useState(false);
  const [showFeelingForm, setShowFeelingForm] = useState(false);
  const [showCategoryForm, setShowCategoryForm] = useState(false);

  // Prayer digest
  const [digestPreview, setDigestPreview] = useState(null);
  const [digestLoading, setDigestLoading] = useState(false);
  const [digestSending, setDigestSending] = useState(false);

  // Intro video
  const [introVideo, setIntroVideo] = useState(null);
  const [videoUploading, setVideoUploading] = useState(false);
  const [videoProgress, setVideoProgress] = useState(0);
  const [transcriptDraft, setTranscriptDraft] = useState('');
  const [savingTranscript, setSavingTranscript] = useState(false);

  // Testimonials / contact messages
  const [adminTestimonials, setAdminTestimonials] = useState([]);
  const [contactMessages, setContactMessages] = useState([]);

  // Scripture library
  const [scriptures, setScriptures] = useState([]);
  const [editingScripture, setEditingScripture] = useState(null);
  const [showScriptureForm, setShowScriptureForm] = useState(false);

  // FAQs
  const [faqs, setFaqs] = useState([]);
  const [editingFaq, setEditingFaq] = useState(null);
  const [showFaqForm, setShowFaqForm] = useState(false);

  // Topics
  const [adminTopics, setAdminTopics] = useState([]);

  // Engagement digest
  const [digestDays, setDigestDays] = useState(1);
  const [digestPreviewData, setDigestPreviewData] = useState(null);
  const [digestSendingComments, setDigestSendingComments] = useState(false);

  // Article publish notification
  const [notifyModal, setNotifyModal] = useState(null);
  const [notifyIntro, setNotifyIntro] = useState('');
  const [notifySending, setNotifySending] = useState(false);
  const [notifyChannels, setNotifyChannels] = useState({ send_email: true, send_push: false });

  // Verse of the Day
  const [activeVerse, setActiveVerse] = useState(null);
  const [verseDraft, setVerseDraft] = useState({ text: '', reference: '', tag: '' });
  const [savingVerse, setSavingVerse] = useState(false);

  // Members
  const [members, setMembers] = useState([]);

  useEffect(() => { loadData(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadData = async () => {
    try {
      const [articlesRes, consultationsRes, feelingsRes, categoriesRes] = await Promise.all([
        api.get('/articles?include_drafts=true'),
        api.get('/consultations'),
        api.get('/feelings'),
        api.get('/categories'),
      ]);
      setArticles(articlesRes.data);
      setConsultations(consultationsRes.data);
      setFeelings(feelingsRes.data);
      setCategories(categoriesRes.data);

      const revenue = consultationsRes.data
        .filter(c => c.payment_status === 'paid')
        .reduce((sum, c) => sum + (c.tier === 'basic' ? 25 : c.tier === 'premium' ? 50 : 100), 0);

      setStats({ articles: articlesRes.data.length, consultations: consultationsRes.data.length, revenue });
    } catch (error) {
      console.error('Failed to load data:', error);
    }
  };

  // ---- Prayer digest ----
  const loadDigestPreview = async () => {
    setDigestLoading(true);
    try { const { data } = await api.get('/admin/prayer-digest/preview'); setDigestPreview(data); }
    catch { toast.error('Could not load digest preview'); }
    finally { setDigestLoading(false); }
  };

  const sendDigest = async () => {
    const total = digestPreview?.recipient_count ?? 0;
    if (!window.confirm(`Send this week's Prayer Wall digest to ${total} subscribed ${total === 1 ? 'person' : 'people'}?`)) return;
    setDigestSending(true);
    try {
      const { data } = await api.post('/admin/prayer-digest/send');
      toast.success(`Digest sent to ${data.sent} of ${data.total_recipients}${data.failed ? ` (${data.failed} failed)` : ''}`);
    } catch (error) {
      const msg = error?.response?.data?.detail || 'Failed to send digest';
      toast.error(typeof msg === 'string' ? msg : 'Failed to send digest');
    } finally { setDigestSending(false); }
  };

  useEffect(() => {
    if (activeTab === 'digest' && !digestPreview && !digestLoading) loadDigestPreview();
    if (activeTab === 'video' && !introVideo) loadIntroVideo();
    if (activeTab === 'stories') loadAdminTestimonials();
    if (activeTab === 'messages') loadContactMessages();
    if (activeTab === 'library') loadScriptures();
    if (activeTab === 'faqs') loadFaqs();
    if (activeTab === 'topics') loadAdminTopics();
    if (activeTab === 'engagement') loadEngagementDigestPreview(digestDays);
    if (activeTab === 'verse') loadVerseOfDay();
    if (activeTab === 'members') loadMembers();
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- Intro video ----
  const loadIntroVideo = async () => {
    try { const { data } = await api.get('/intro-video'); setIntroVideo(data); setTranscriptDraft(data?.captions || ''); }
    catch { setIntroVideo(null); }
  };

  const extractPosterFrame = (file) => new Promise((resolve) => {
    try {
      const url = URL.createObjectURL(file);
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.muted = true;
      video.playsInline = true;
      video.src = url;
      const cleanup = () => URL.revokeObjectURL(url);
      video.addEventListener('loadedmetadata', () => {
        video.currentTime = Math.min(1, (video.duration || 2) * 0.1);
      });
      video.addEventListener('seeked', () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = Math.min(1280, video.videoWidth || 1280);
          canvas.height = Math.round(canvas.width * ((video.videoHeight || 720) / (video.videoWidth || 1280)));
          const ctx = canvas.getContext('2d');
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          canvas.toBlob((blob) => { cleanup(); resolve(blob); }, 'image/jpeg', 0.85);
        } catch (err) { cleanup(); resolve(null); }
      });
      video.addEventListener('error', () => { cleanup(); resolve(null); });
      setTimeout(() => { cleanup(); resolve(null); }, 15000);
    } catch { resolve(null); }
  });

  const uploadIntroVideo = async (file) => {
    if (!file) return;
    if (file.size > 200 * 1024 * 1024) {
      toast.error('Video is too large. Please compress to under 200MB (Handbrake or QuickTime export at 720p works).');
      return;
    }
    setVideoUploading(true);
    setVideoProgress(0);
    try {
      toast.message('Preparing thumbnail…');
      const thumbBlob = await extractPosterFrame(file);
      const form = new FormData();
      form.append('file', file);
      if (thumbBlob) form.append('thumbnail', thumbBlob, 'poster.jpg');

      await api.post('/admin/intro-video', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 10 * 60 * 1000, // 10 minutes for large uploads
        onUploadProgress: (e) => { if (e.total) setVideoProgress(Math.round((e.loaded * 100) / e.total)); },
      });
      toast.success(thumbBlob ? 'Intro video + thumbnail uploaded' : 'Intro video uploaded');
      loadIntroVideo();
    } catch (error) {
      const status = error?.response?.status;
      const detail = error?.response?.data?.detail;
      let msg = 'Video upload failed';
      if (status === 413 || detail?.toLowerCase?.().includes('too large')) {
        msg = detail || 'Video is too large — please compress it to under 200MB.';
      } else if (error?.code === 'ECONNABORTED' || error?.message?.toLowerCase?.().includes('timeout')) {
        msg = 'Upload timed out. Try a smaller/compressed MP4, or check your internet connection.';
      } else if (typeof detail === 'string') {
        msg = detail;
      } else if (error?.message) {
        msg = `Upload failed: ${error.message}`;
      }
      toast.error(msg);
    } finally { setVideoUploading(false); setVideoProgress(0); }
  };

  const saveTranscript = async () => {
    if (!introVideo) { toast.error('Upload a video first'); return; }
    setSavingTranscript(true);
    try { await api.put('/admin/intro-video/captions', { captions: transcriptDraft }); toast.success('Transcript saved'); loadIntroVideo(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Could not save transcript'); }
    finally { setSavingTranscript(false); }
  };

  const removeIntroVideo = async () => {
    if (!window.confirm('Remove the current intro video?')) return;
    try { await api.delete('/admin/intro-video'); toast.success('Intro video removed'); setIntroVideo(null); setTranscriptDraft(''); }
    catch { toast.error('Failed to remove video'); }
  };

  // ---- Testimonials ----
  const loadAdminTestimonials = async () => { try { const { data } = await api.get('/admin/testimonials'); setAdminTestimonials(data); } catch {} };
  const approveTestimonial = async (id) => { try { await api.put(`/admin/testimonials/${id}/approve`); toast.success('Story approved'); loadAdminTestimonials(); } catch { toast.error('Failed to approve'); } };
  const deleteTestimonial = async (id) => {
    if (!window.confirm('Delete this testimonial? This cannot be undone.')) return;
    try { await api.delete(`/admin/testimonials/${id}`); toast.success('Testimonial removed'); loadAdminTestimonials(); }
    catch { toast.error('Failed to delete'); }
  };

  // ---- Contact messages ----
  const loadContactMessages = async () => { try { const { data } = await api.get('/admin/contact-messages'); setContactMessages(data); } catch {} };
  const markContactRead = async (id) => { try { await api.put(`/admin/contact-messages/${id}/read`); loadContactMessages(); } catch { toast.error('Failed to mark read'); } };
  const deleteContactMessage = async (id) => {
    if (!window.confirm('Delete this message?')) return;
    try { await api.delete(`/admin/contact-messages/${id}`); toast.success('Message deleted'); loadContactMessages(); }
    catch { toast.error('Failed to delete'); }
  };

  // ---- Scripture library ----
  const loadScriptures = async () => { try { const { data } = await api.get('/admin/scriptures'); setScriptures(data); } catch {} };
  const openScriptureForm = (s = null) => { setEditingScripture(s || { text: '', reference: '', tag: '' }); setShowScriptureForm(true); };
  const saveScripture = async () => {
    if (!editingScripture.text.trim() || !editingScripture.reference.trim()) { toast.error('Text and reference are required'); return; }
    try {
      if (editingScripture.id) { await api.put(`/admin/scriptures/${editingScripture.id}`, editingScripture); toast.success('Scripture updated'); }
      else { await api.post('/admin/scriptures', editingScripture); toast.success('Scripture saved'); }
      setShowScriptureForm(false); setEditingScripture(null); loadScriptures();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Could not save scripture'); }
  };
  const deleteScripture = async (id) => {
    if (!window.confirm('Delete this scripture from your library?')) return;
    try { await api.delete(`/admin/scriptures/${id}`); toast.success('Removed from library'); loadScriptures(); }
    catch { toast.error('Failed to delete'); }
  };
  const insertScriptureIntoFeeling = (s) => {
    if (!editingFeeling) return;
    const scriptureList = [...editingFeeling.scriptures];
    const emptyIdx = scriptureList.findIndex(x => !x.text && !x.reference);
    if (emptyIdx >= 0) scriptureList[emptyIdx] = { text: s.text, reference: s.reference };
    else scriptureList.push({ text: s.text, reference: s.reference });
    setEditingFeeling({ ...editingFeeling, scriptures: scriptureList });
    toast.success('Scripture added');
  };

  // ---- FAQs ----
  const loadFaqs = async () => { try { const { data } = await api.get('/faqs'); setFaqs(data); } catch {} };
  const openFaqForm = (f = null) => { setEditingFaq(f || { question: '', answer: '', order: faqs.length }); setShowFaqForm(true); };
  const saveFaq = async () => {
    if (!editingFaq.question.trim() || !editingFaq.answer.trim()) { toast.error('Question and answer are both required'); return; }
    try {
      if (editingFaq.id) { await api.put(`/admin/faqs/${editingFaq.id}`, editingFaq); toast.success('FAQ updated'); }
      else { await api.post('/admin/faqs', editingFaq); toast.success('FAQ added'); }
      setShowFaqForm(false); setEditingFaq(null); loadFaqs();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Could not save'); }
  };
  const deleteFaq = async (id) => {
    if (!window.confirm('Delete this FAQ?')) return;
    try { await api.delete(`/admin/faqs/${id}`); toast.success('FAQ deleted'); loadFaqs(); }
    catch { toast.error('Failed to delete'); }
  };

  // ---- Topics ----
  const loadAdminTopics = async () => { try { const { data } = await api.get('/admin/topics'); setAdminTopics(data); } catch {} };
  const updateTopicStatus = async (id, status) => { try { await api.put(`/admin/topics/${id}/status`, { status }); toast.success('Updated'); loadAdminTopics(); } catch { toast.error('Could not update'); } };
  const removeTopic = async (id) => {
    if (!window.confirm('Delete this topic idea?')) return;
    try { await api.delete(`/admin/topics/${id}`); toast.success('Deleted'); loadAdminTopics(); }
    catch { toast.error('Failed to delete'); }
  };

  // ---- Engagement digest ----
  const loadEngagementDigestPreview = async (days = 1) => {
    try { const { data } = await api.get('/admin/engagement/digest/preview', { params: { days } }); setDigestPreviewData(data); } catch {}
  };
  const sendEngagementDigest = async () => {
    setDigestSendingComments(true);
    try {
      const { data } = await api.post('/admin/engagement/digest/send', null, { params: { days: digestDays } });
      toast.success(`Digest sent to ${data.sent_to}`);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Could not send'); }
    finally { setDigestSendingComments(false); }
  };

  // ---- Members ----
  const loadMembers = async () => { try { const { data } = await api.get('/admin/users'); setMembers(data); } catch {} };
  const deleteMember = async (member) => {
    if (!window.confirm(`Permanently delete ${member.email}?\n\nThis removes their account and cannot be undone. Their public prayer requests and comments will stay.`)) return;
    try {
      await api.delete(`/admin/users/${member.id}`);
      toast.success(`${member.email} removed`);
      loadMembers();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not delete member');
    }
  };
  const bulkDeleteMembers = async (ids) => {
    if (!window.confirm(`Delete ${ids.length} member account${ids.length === 1 ? '' : 's'}? This cannot be undone.`)) return false;
    try {
      const { data } = await api.post('/admin/users/bulk-delete', { user_ids: ids });
      toast.success(`Removed ${data.deleted} of ${data.requested} member${data.requested === 1 ? '' : 's'}`);
      loadMembers();
      return true;
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Bulk delete failed');
      return false;
    }
  };
  const sendWelcomeToAll = async (count) => {
    if (!window.confirm(`Send the standard welcome email to ${count} member${count === 1 ? '' : 's'} who haven't received one yet?`)) return;
    try {
      const { data } = await api.post('/admin/users/send-welcome', { only_never_received: true });
      const msg = data.sent > 0
        ? `Welcome email sent to ${data.sent}${data.failed ? ` (${data.failed} failed)` : ''}`
        : data.message || 'No members needed a welcome';
      toast.success(msg);
      loadMembers();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not send welcome emails');
    }
  };
  const sendWelcomeToSelected = async (ids) => {
    if (!window.confirm(`Send the welcome email to ${ids.length} selected member${ids.length === 1 ? '' : 's'}?\n\nThis sends the standard welcome even if they've received one before.`)) return false;
    try {
      const { data } = await api.post('/admin/users/send-welcome', { user_ids: ids, only_never_received: false });
      toast.success(`Sent ${data.sent} of ${data.total_targets}${data.failed ? ` (${data.failed} failed)` : ''}`);
      loadMembers();
      return true;
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not send welcome emails');
      return false;
    }
  };
  const sendTestEmail = async (email, kind) => {
    try {
      await api.post('/admin/test-email', { email, kind });
      return true;
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Could not send test email';
      toast.error(typeof detail === 'string' ? detail : 'Could not send test email');
      return false;
    }
  };

  // ---- Verse of the Day ----
  const loadVerseOfDay = async () => {
    try {
      const { data } = await api.get('/verse-of-day');
      setActiveVerse(data);
      if (data) setVerseDraft({ text: data.text, reference: data.reference, tag: data.tag || '' });
      else setVerseDraft({ text: '', reference: '', tag: '' });
    } catch {}
  };
  const saveVerseOfDay = async () => {
    if (!verseDraft.text.trim() || !verseDraft.reference.trim()) { toast.error('Both verse and reference are required'); return; }
    setSavingVerse(true);
    try { await api.put('/admin/verse-of-day', verseDraft); toast.success("Verse of the Week updated — it's live on the home page."); loadVerseOfDay(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Could not save verse'); }
    finally { setSavingVerse(false); }
  };
  const clearVerseOfDay = async () => {
    if (!window.confirm('Hide the current Verse of the Week from the home page?')) return;
    try { await api.delete('/admin/verse-of-day'); toast.success('Verse hidden from the home page'); setActiveVerse(null); setVerseDraft({ text: '', reference: '', tag: '' }); }
    catch { toast.error('Could not hide verse'); }
  };

  // ---- Articles ----
  const deleteArticle = async (id) => {
    if (!window.confirm('Are you sure you want to delete this article?')) return;
    try { await api.delete(`/articles/${id}`); toast.success('Article deleted'); loadData(); }
    catch { toast.error('Failed to delete article'); }
  };
  const openArticleForm = (article = null) => {
    let sched = '';
    if (article?.scheduled_for) {
      const d = new Date(article.scheduled_for);
      const pad = (n) => String(n).padStart(2, '0');
      sched = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    }
    setEditingArticle(article
      ? { ...article, scheduled_for: sched, feelings: article.feelings || [], relationships: article.relationships || [] }
      : { title: '', category: 'dating-engagement', content: '', scriptures: [{ text: '', reference: '' }], status: 'published', scheduled_for: '', feelings: [], relationships: [] }
    );
    setShowArticleForm(true);
  };
  const openFeelingForm = (feeling = null) => {
    if (scriptures.length === 0) loadScriptures();
    setEditingFeeling(feeling || { feeling: 'sad', who_about: 'spouse', guidance: '', scriptures: [{ text: '', reference: '' }] });
    setShowFeelingForm(true);
  };
  const openCategoryForm = (category) => { setEditingCategory({ ...category }); setShowCategoryForm(true); };

  const saveArticle = async (asDraft = false) => {
    try {
      const cleanScriptures = editingArticle.scriptures.filter(s => s.text && s.reference);
      let scheduledIso = null;
      if (!asDraft && editingArticle.scheduled_for) scheduledIso = new Date(editingArticle.scheduled_for).toISOString();
      const payload = {
        title: editingArticle.title,
        category: editingArticle.category,
        content: editingArticle.content,
        scriptures: cleanScriptures,
        status: asDraft ? 'draft' : 'published',
        scheduled_for: scheduledIso,
        feelings: editingArticle.feelings || [],
        relationships: editingArticle.relationships || [],
      };
      const isScheduled = !!scheduledIso;
      let articleId = editingArticle.id;
      if (editingArticle.id) {
        await api.put(`/articles/${editingArticle.id}`, payload);
        toast.success(asDraft ? 'Draft saved' : isScheduled ? `Scheduled for ${new Date(scheduledIso).toLocaleString()}` : 'Article updated');
      } else {
        const { data } = await api.post('/articles', payload);
        articleId = data.id;
        toast.success(asDraft ? 'Draft saved' : isScheduled ? `Scheduled for ${new Date(scheduledIso).toLocaleString()}` : 'Article published');
      }
      setShowArticleForm(false); setEditingArticle(null); loadData();
      if (!asDraft && !isScheduled && articleId) openNotifyModal(articleId);
    } catch (error) { toast.error('Failed to save article'); }
  };

  const openNotifyModal = async (articleId) => {
    try { const { data } = await api.get(`/admin/articles/${articleId}/notify-preview`); setNotifyIntro(data.default_intro || ''); setNotifyChannels({ send_email: true, send_push: false }); setNotifyModal({ articleId, preview: data }); }
    catch { /* silent */ }
  };

  const sendNotification = async () => {
    if (!notifyModal) return;
    setNotifySending(true);
    try {
      const { data } = await api.post(`/admin/articles/${notifyModal.articleId}/notify`, { intro: notifyIntro, ...notifyChannels });
      toast.success(`${notifyChannels.send_email ? `Emailed ${data.sent} of ${data.total_recipients}${data.failed ? ` (${data.failed} failed)` : ''}` : 'Sent'}${describePushResult(data.push)}`);
      setNotifyModal(null);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Could not send announcements'); }
    finally { setNotifySending(false); }
  };

  const saveCategory = async () => {
    try {
      await api.put(`/categories/${editingCategory.id}`, {
        name: editingCategory.name, description: editingCategory.description, icon: editingCategory.icon,
        verse_text: editingCategory.verse_text, verse_reference: editingCategory.verse_reference,
      });
      toast.success('Category updated');
      setShowCategoryForm(false); setEditingCategory(null); loadData();
    } catch (error) { toast.error('Failed to update category'); }
  };

  const saveFeeling = async (alsoSend = false) => {
    try {
      const cleanScriptures = editingFeeling.scriptures
        .filter(s => (s.text || '').trim())
        .map(s => ({ text: s.text.trim(), reference: (s.reference || '').trim() }));
      const payload = { feeling: editingFeeling.feeling, who_about: editingFeeling.who_about, topic: (editingFeeling.topic || '').trim() || null, guidance: editingFeeling.guidance, scriptures: cleanScriptures };
      const channels = editingFeeling.channels || { send_email: true, send_push: false };
      let saved;
      if (editingFeeling.id) { saved = await api.put(`/feelings/${editingFeeling.id}`, payload); toast.success('Feelings guidance updated'); }
      else { saved = await api.post('/feelings', payload); toast.success('Feelings guidance added'); }
      setShowFeelingForm(false); setEditingFeeling(null);
      await loadData();
      if (alsoSend) {
        const feelingId = saved?.data?.id || editingFeeling.id;
        if (!feelingId) { toast.error('Could not find the saved guide to send'); return; }
        try {
          const res = await api.post(`/admin/feelings/${feelingId}/notify/send`, channels);
          const { sent, failed, total_recipients, push } = res.data;
          toast.success(`${channels.send_email ? `Emailed ${sent} of ${total_recipients}` : 'Sent'}${describePushResult(push)}`);
          if (failed > 0) toast.error(`${failed} failed to send`);
        } catch (sendErr) {
          toast.error(sendErr?.response?.data?.detail || 'Save succeeded, but sending failed');
        }
      }
    } catch (error) { toast.error('Failed to save feelings guidance'); }
  };

  const deleteFeeling = async (id) => {
    if (!window.confirm('Delete this Feelings guidance entry? This cannot be undone.')) return;
    try { await api.delete(`/feelings/${id}`); toast.success('Guidance deleted'); loadData(); }
    catch (error) { toast.error('Failed to delete guidance'); }
  };

  const updateScripture = (index, field, value, target) => {
    const updated = { ...target };
    updated.scriptures[index][field] = value;
    if (target === editingArticle) setEditingArticle(updated); else setEditingFeeling(updated);
  };
  const addScripture = (target) => {
    const updated = { ...target };
    updated.scriptures.push({ text: '', reference: '' });
    if (target === editingArticle) setEditingArticle(updated); else setEditingFeeling(updated);
  };
  const removeScripture = (index, target) => {
    const updated = { ...target };
    updated.scriptures.splice(index, 1);
    if (target === editingArticle) setEditingArticle(updated); else setEditingFeeling(updated);
  };

  const pendingConsultations = consultations.filter(c => c.status === 'pending');

  // Quick actions for the Overview
  const quickActions = [
    { key: 'new-article', label: 'New Article', hint: 'Write and publish', tint: '#E57A44', Icon: BookOpen, onClick: () => openArticleForm() },
    { key: 'sacred-tools', label: 'Sacred Tools', hint: 'Add topics & tools', tint: '#78A18E', Icon: Package, onClick: () => navigate('/admin/tools') },
    { key: 'meet-genesis', label: 'Meet Genesis', hint: 'Edit your About page', tint: '#B38A2E', Icon: User, onClick: () => navigate('/admin/meet-genesis') },
    { key: 'site-content', label: 'Site Copy', hint: 'Edit Sanctuary & page text', tint: '#5B7C99', Icon: PenSquare, onClick: () => navigate('/admin/site-content') },
    { key: 'fellowship-mod', label: 'Fellowship Moderation', hint: 'Review flagged posts', tint: '#B77BB0', Icon: ShieldCheck, onClick: () => navigate('/admin/fellowship-moderation') },
    { key: 'article-requests', label: 'Content Requests', hint: 'What people want written', tint: '#78A18E', Icon: MessageSquareText, onClick: () => navigate('/admin/article-requests') },
    { key: 'bulk-tag', label: 'Bulk Tag Articles', hint: 'Wire articles to the Journey wizard', tint: '#B77BB0', Icon: Tag, onClick: () => navigate('/admin/bulk-tag') },
    { key: 'connection-room', label: 'Connection Room', hint: 'Weekly prompts, polls & anon Q&A', tint: '#E57A44', Icon: MessageSquareText, onClick: () => navigate('/admin/connection-room') },
    { key: 'prayer-emails', label: 'Prayer Emails', hint: 'Write & send prayers to members', tint: '#B8564A', Icon: Sparkles, onClick: () => navigate('/admin/prayer-emails') },
    { key: 'subscribers', label: 'Subscribers', hint: 'Newsletter list & welcome email', tint: '#B8891A', Icon: Mail, onClick: () => navigate('/admin/subscribers') },
    { key: 'resources', label: 'Resources', hint: 'Crisis + support hotlines', tint: '#8C5E5E', Icon: LifeBuoy, onClick: () => navigate('/admin/resources') },
    { key: 'plan-post', label: 'Plan a Post', hint: 'Schedule what to write', tint: '#5B7C99', Icon: CalendarClock, onClick: () => setActiveTab('calendar') },
    { key: 'reading-trends', label: 'Reading Trends', hint: 'See what people love', tint: '#B38A2E', Icon: BarChart3, onClick: () => setActiveTab('analytics') },
  ];

  const alertActionHandlers = {
    goto_verse: () => setActiveTab('verse'),
    goto_consultations: () => setActiveTab('consultations'),
    goto_messages: () => setActiveTab('messages'),
    goto_stories: () => setActiveTab('stories'),
    goto_topics: () => setActiveTab('topics'),
  };
  const handleAlertAction = (actionType) => {
    const fn = alertActionHandlers[actionType];
    if (fn) fn();
  };

  const renderTab = () => {
    switch (activeTab) {
      case 'overview': return <OverviewTab pendingConsultations={pendingConsultations} quickActions={quickActions} alertsPanel={<AlertsPanel onAction={handleAlertAction} onOpenArticle={() => openArticleForm()} />} />;
      case 'calendar': return <ContentCalendarTab />;
      case 'analytics': return <AnalyticsTab />;
      case 'earnings': return <EarningsTab />;
      case 'articles': return <ArticlesTab articles={articles} categories={categories} openArticleForm={openArticleForm} deleteArticle={deleteArticle} openNotifyModal={openNotifyModal} />;
      case 'verse': return <VerseOfDayTab verseDraft={verseDraft} setVerseDraft={setVerseDraft} savingVerse={savingVerse} saveVerseOfDay={saveVerseOfDay} clearVerseOfDay={clearVerseOfDay} activeVerse={activeVerse} />;
      case 'categories': return <CategoriesTab categories={categories} openCategoryForm={openCategoryForm} />;
      case 'feelings': return <FeelingsTab feelings={feelings} openFeelingForm={openFeelingForm} deleteFeeling={deleteFeeling} />;
      case 'consultations': return <ConsultationsTab consultations={consultations} />;
      case 'digest': return <PrayerDigestTab digestPreview={digestPreview} digestLoading={digestLoading} digestSending={digestSending} loadDigestPreview={loadDigestPreview} sendDigest={sendDigest} />;
      case 'video': return <VideoTab introVideo={introVideo} videoUploading={videoUploading} videoProgress={videoProgress} transcriptDraft={transcriptDraft} setTranscriptDraft={setTranscriptDraft} savingTranscript={savingTranscript} uploadIntroVideo={uploadIntroVideo} saveTranscript={saveTranscript} removeIntroVideo={removeIntroVideo} />;
      case 'library': return <ScriptureLibraryTab scriptures={scriptures} openScriptureForm={openScriptureForm} deleteScripture={deleteScripture} />;
      case 'messages': return <ContactMessagesTab contactMessages={contactMessages} loadContactMessages={loadContactMessages} markContactRead={markContactRead} deleteContactMessage={deleteContactMessage} />;
      case 'faqs': return <FAQsTab faqs={faqs} openFaqForm={openFaqForm} deleteFaq={deleteFaq} />;
      case 'topics': return <TopicsTab adminTopics={adminTopics} updateTopicStatus={updateTopicStatus} removeTopic={removeTopic} />;
      case 'engagement': return <EngagementTab digestDays={digestDays} setDigestDays={setDigestDays} loadEngagementDigestPreview={loadEngagementDigestPreview} digestPreviewData={digestPreviewData} digestSendingComments={digestSendingComments} sendEngagementDigest={sendEngagementDigest} />;
      case 'stories': return <StoriesTab adminTestimonials={adminTestimonials} loadAdminTestimonials={loadAdminTestimonials} approveTestimonial={approveTestimonial} deleteTestimonial={deleteTestimonial} />;
      case 'members': return <MembersTab members={members} loadMembers={loadMembers} deleteMember={deleteMember} bulkDeleteMembers={bulkDeleteMembers} sendWelcomeToAll={sendWelcomeToAll} sendWelcomeToSelected={sendWelcomeToSelected} sendTestEmail={sendTestEmail} />;
      default: return null;
    }
  };

  const activeGroup = TAB_TO_GROUP[activeTab] || NAV_GROUPS[0];
  const activeTabMeta = activeGroup.tabs.find(t => t.key === activeTab) || activeGroup.tabs[0];

  const toggleGroup = (key) => setExpandedGroups(prev => ({ ...prev, [key]: !prev[key] }));

  const selectTab = (key) => {
    setActiveTab(key);
    setMobileNavOpen(false);
  };

  return (
    <div style={{ backgroundColor: '#FDFBF7', minHeight: '100vh' }}>
      <Header />

      {/* Tranquil nature hero banner */}
      <div className="relative overflow-hidden" style={{ height: '260px' }}>
        <img
          src="https://images.unsplash.com/photo-1470071459604-3b5ec3a7fe05?auto=format&fit=crop&w=2000&q=70"
          alt="Misty mountains at sunrise"
          className="absolute inset-0 w-full h-full object-cover"
          loading="eager"
        />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(44,42,41,0.35) 0%, rgba(44,42,41,0.55) 60%, rgba(253,251,247,1) 100%)' }} />
        {/* Layered mountain silhouettes */}
        <svg className="absolute bottom-16 left-0 w-full opacity-40" height="80" viewBox="0 0 1440 80" preserveAspectRatio="none" fill="none" aria-hidden="true">
          <path d="M0 80 L120 40 L220 60 L340 20 L470 55 L610 30 L760 60 L900 25 L1040 55 L1200 30 L1330 55 L1440 40 L1440 80 Z" fill="#5B7C99" />
        </svg>
        <div className="relative max-w-7xl mx-auto px-6 h-full flex flex-col justify-center">
          <div className="flex items-center gap-2 mb-2" style={{ color: '#FDFBF7' }}>
            <Waves className="w-4 h-4" />
            <span className="text-xs uppercase tracking-widest" style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 600, letterSpacing: '0.15em' }}>Admin Sanctuary</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold mb-1" style={{ fontFamily: 'Merriweather, serif', color: '#FDFBF7', textShadow: '0 2px 12px rgba(0,0,0,0.3)' }}>
            Admin Dashboard
          </h1>
          <p className="text-sm max-w-2xl" style={{ fontFamily: 'Nunito, sans-serif', color: '#F0EBE0' }}>
            Tend to your community with a calm heart. Everything you create here reaches real people looking for hope.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 -mt-10 relative z-10 pb-16">
        {/* Stat cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <StatCard icon={BookOpen} tint="#E57A44" label="Articles" value={stats.articles} sub="published & drafts" />
          <StatCard icon={MessageCircle} tint="#78A18E" label="Consultations" value={stats.consultations} sub={`${pendingConsultations.length} pending`} />
          <StatCard icon={DollarSign} tint="#5B7C99" label="Revenue" value={`$${stats.revenue}`} sub="all-time paid" />
        </div>

        {/* Privacy banner */}
        <div className="flex items-center gap-2 mb-6 px-4 py-2 rounded-full inline-flex" style={{ backgroundColor: '#FFFFFF', border: '1px solid #E6E2D8', boxShadow: '0 2px 8px rgba(0,0,0,0.03)' }}>
          <Lock className="w-3.5 h-3.5" style={{ color: '#78A18E' }} />
          <span style={{ fontFamily: 'Nunito, sans-serif', color: '#595553', fontSize: '12px' }}>
            Private. Only you can create, edit, and delete content.
          </span>
        </div>

        {/* Mobile nav button */}
        <div className="lg:hidden mb-4">
          <button
            onClick={() => setMobileNavOpen(!mobileNavOpen)}
            className="w-full flex items-center justify-between px-4 py-3 rounded-2xl"
            style={{ backgroundColor: '#FFFFFF', border: '1px solid #E6E2D8', fontFamily: 'Nunito, sans-serif', color: '#2C2A29' }}
            data-testid="admin-mobile-nav-toggle"
          >
            <span className="flex items-center gap-2 font-semibold">
              <activeTabMeta.Icon className="w-4 h-4" style={{ color: activeGroup.tint }} />
              <span>{activeGroup.label} · {activeTabMeta.label}</span>
            </span>
            {mobileNavOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
          </button>
        </div>

        <div className="flex flex-col lg:flex-row gap-6">
          {/* Sidebar navigation */}
          <aside className={`${mobileNavOpen ? 'block' : 'hidden'} lg:block lg:w-64 lg:flex-shrink-0`}>
            <div className="lg:sticky lg:top-6 space-y-2 p-3 rounded-3xl" style={{ backgroundColor: '#FFFFFF', boxShadow: '0 8px 30px rgba(0,0,0,0.04)', border: '1px solid #EFEAE0' }}>
              {NAV_GROUPS.map((group) => {
                const expanded = expandedGroups[group.key];
                const hasActive = group.tabs.some(t => t.key === activeTab);
                return (
                  <div key={group.key}>
                    <button
                      onClick={() => toggleGroup(group.key)}
                      className="w-full flex items-center justify-between px-3 py-2.5 rounded-xl transition-colors"
                      style={{
                        backgroundColor: hasActive ? `${group.tint}12` : 'transparent',
                        fontFamily: 'Manrope, sans-serif',
                        color: '#2C2A29',
                      }}
                      data-testid={`admin-group-${group.key}`}
                    >
                      <span className="flex items-center gap-2.5">
                        <span className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${group.tint}20` }}>
                          <group.Icon className="w-3.5 h-3.5" style={{ color: group.tint }} />
                        </span>
                        <span className="text-sm font-bold uppercase tracking-wider" style={{ letterSpacing: '0.08em', fontSize: '11px' }}>{group.label}</span>
                      </span>
                      <ChevronDown className={`w-4 h-4 transition-transform ${expanded ? '' : '-rotate-90'}`} style={{ color: '#8C867D' }} />
                    </button>
                    {expanded && (
                      <div className="mt-1 mb-2 ml-2 pl-3 space-y-0.5" style={{ borderLeft: `2px solid ${group.tint}20` }}>
                        {group.tabs.map((tab) => {
                          const isActive = !tab.href && activeTab === tab.key;
                          const handleClick = tab.href ? () => navigate(tab.href) : () => selectTab(tab.key);
                          return (
                            <button
                              key={tab.key}
                              onClick={handleClick}
                              className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg transition-all duration-200"
                              style={{
                                backgroundColor: isActive ? group.tint : 'transparent',
                                color: isActive ? '#FDFBF7' : '#595553',
                                fontFamily: 'Nunito, sans-serif',
                                fontWeight: isActive ? 700 : 500,
                                fontSize: '13.5px',
                                boxShadow: isActive ? `0 4px 12px ${group.tint}55` : 'none',
                              }}
                              data-testid={`admin-tab-${tab.key}`}
                            >
                              <tab.Icon className="w-3.5 h-3.5 flex-shrink-0" strokeWidth={isActive ? 2.4 : 2} />
                              <span className="truncate text-left">{tab.label}</span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Small nature accent at bottom */}
              <div className="pt-3 mt-2 flex items-center justify-center gap-1.5" style={{ borderTop: '1px dashed #E6E2D8' }}>
                <Waves className="w-3 h-3" style={{ color: '#78A18E' }} />
                <span className="text-[10px] uppercase tracking-widest" style={{ fontFamily: 'Nunito, sans-serif', color: '#8C867D', letterSpacing: '0.15em' }}>Stay grounded</span>
                <Mountain className="w-3 h-3" style={{ color: '#5B7C99' }} />
              </div>
            </div>
          </aside>

          {/* Content area */}
          <main className="flex-1 min-w-0">
            {/* Section header */}
            <div className="mb-5 flex items-center gap-3">
              <div className="w-11 h-11 rounded-2xl flex items-center justify-center" style={{ backgroundColor: `${activeGroup.tint}18` }}>
                <activeTabMeta.Icon className="w-5 h-5" style={{ color: activeGroup.tint }} />
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-widest font-semibold" style={{ fontFamily: 'Nunito, sans-serif', color: activeGroup.tint, letterSpacing: '0.15em' }}>
                  {activeGroup.label}
                </p>
                <h2 className="text-2xl font-bold" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>
                  {activeTabMeta.label}
                </h2>
              </div>
            </div>

            {renderTab()}
          </main>
        </div>
      </div>

      <ArticleFormModal editingArticle={showArticleForm ? editingArticle : null} setEditingArticle={setEditingArticle} categories={categories} saveArticle={saveArticle} addScripture={addScripture} removeScripture={removeScripture} updateScripture={updateScripture} onClose={() => { setShowArticleForm(false); setEditingArticle(null); }} />
      <FeelingFormModal editingFeeling={showFeelingForm ? editingFeeling : null} setEditingFeeling={setEditingFeeling} scriptures={scriptures} insertScriptureIntoFeeling={insertScriptureIntoFeeling} addScripture={addScripture} removeScripture={removeScripture} updateScripture={updateScripture} saveFeeling={saveFeeling} onClose={() => { setShowFeelingForm(false); setEditingFeeling(null); }} />
      <CategoryFormModal editingCategory={showCategoryForm ? editingCategory : null} setEditingCategory={setEditingCategory} saveCategory={saveCategory} onClose={() => { setShowCategoryForm(false); setEditingCategory(null); }} />
      <ScriptureFormModal editingScripture={showScriptureForm ? editingScripture : null} setEditingScripture={setEditingScripture} saveScripture={saveScripture} onClose={() => { setShowScriptureForm(false); setEditingScripture(null); }} />
      <NotifyModal notifyModal={notifyModal} notifyIntro={notifyIntro} setNotifyIntro={setNotifyIntro} notifySending={notifySending} notifyChannels={notifyChannels} setNotifyChannels={setNotifyChannels} sendNotification={sendNotification} onClose={() => setNotifyModal(null)} />
      <FAQFormModal editingFaq={showFaqForm ? editingFaq : null} setEditingFaq={setEditingFaq} saveFaq={saveFaq} onClose={() => { setShowFaqForm(false); setEditingFaq(null); }} />

      <Footer />
    </div>
  );
}

function StatCard({ icon: Icon, tint, label, value, sub }) {
  return (
    <div className="p-5 rounded-2xl relative overflow-hidden" style={{ backgroundColor: '#FFFFFF', boxShadow: '0 8px 30px rgba(0, 0, 0, 0.06)', border: '1px solid #EFEAE0' }}>
      <div className="absolute -top-8 -right-8 w-24 h-24 rounded-full opacity-10" style={{ backgroundColor: tint }} />
      <div className="relative flex items-center gap-4">
        <div className="p-3 rounded-2xl" style={{ backgroundColor: `${tint}20` }}>
          <Icon className="w-6 h-6" style={{ color: tint }} />
        </div>
        <div>
          <p className="text-xs uppercase tracking-widest" style={{ fontFamily: 'Nunito, sans-serif', color: '#8C867D', letterSpacing: '0.1em' }}>{label}</p>
          <p className="text-3xl font-bold leading-tight" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>{value}</p>
          {sub && <p className="text-[11px]" style={{ fontFamily: 'Nunito, sans-serif', color: '#8C867D' }}>{sub}</p>}
        </div>
      </div>
    </div>
  );
}
